// src/pages/InvestorDemoPage.tsx
import React, { useEffect, useRef, useState } from 'react';

import thumbEN from '../assets/video_thumb.jpg';
import {  getInitialLang } from '../Common';
import { checkIfHasInvestorAccess } from './support/checkIfHasInvestorAccess'
import { $MAX_WIDTH } from '../Consts';

const EN = {
  title: 'Product Demo',
  tagline: 'Design, Build and Deploy Enterprise Apps without Months of Coding',
  features: ['Zero Coding, Maximal Control', 'No need for developers, databases or hosting', 'Built for teams and enterprises'],
  video: 'https://serve.businessa.com/batrailer_en.mp4',
  thumb: thumbEN,
  download: 'Download video',
  uid: 'c33ddd8e82de2b027524d6559d284ef2',
};

const ZH = {
  title: '产品演示',
  tagline: '无需耗费数月编码，即可设计、构建并部署企业级应用',
  features: [
    '零代码，极致掌控',
    '无需开发人员、数据库或托管',
    '专为团队和企业打造'
  ],
  video: 'https://serve.businessa.com/batrailer_zh.mp4',
  thumb: thumbEN,
  download: '下载视频',
  uid: '7fff024c38987e06246587cdcc3f6674',
};
const LANGS = { en: EN, zh: ZH };

const InvestorDemoPage: React.FC = () => {
  const [lang] = useState<'en' | 'zh'>(getInitialLang);
  const [hasInvestorAccess] = useState<boolean>(() => checkIfHasInvestorAccess());
  const [token, setToken] = useState<string | null>(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const t = LANGS[lang];

  useEffect(() => {
    if (hasInvestorAccess) return;

    const authUrl = new URL('/auth.html', window.location.origin);
    authUrl.searchParams.set('url', window.location.href);

    window.location.replace(authUrl.href);
  }, [hasInvestorAccess]);

  useEffect(() => {
    const timeout = setTimeout(() => setShowOverlay(false), 10000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!hasInvestorAccess) return;
    let cancel = false;
    (async () => {
      const r = await fetch(`https://businessa.com?get_demo_video_token=1&uid=${t.uid}`);
      const j = await r.json();
      if (!cancel && j.token) setToken(j.token);
    })();
    return () => {
      cancel = true;
    };
  }, [hasInvestorAccess, t.uid]);

  useEffect(() => {
    if (!iframeRef.current) return;
    const onLoad = () => setShowOverlay(false);
    iframeRef.current.addEventListener('load', onLoad);
    return () => {
      iframeRef.current?.removeEventListener('load', onLoad);
    };
  }, [token]);

  if (!hasInvestorAccess) return null;

  const posterEncoded =
    typeof window !== 'undefined'
      ? encodeURIComponent(new URL(t.thumb, window.location.origin).href)
      : '';

      const spinner = (
        <svg width="70" height="70" viewBox="0 0 50 50">
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
          `}</style>
      
          {/* light-gray track */}
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="rgba(255,255,255,0.6)"
            strokeWidth="4"
          />
      
          {/* blue arc */}
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="#0A7CFF"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="110 125"   /* only ~47% of the circle is painted */
            style={{
              transformOrigin: 'center',
              animation: 'spin 1s linear infinite',
            }}
          />
        </svg>
      );

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: '#FFFFFF',
    color: '#0B1E3C',
    display: 'flex',
    flexDirection: 'column',
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
  };
  const heroStyle: React.CSSProperties = {
    maxWidth: $MAX_WIDTH,
    margin: '0 auto',
    paddingTop: '5.5rem',
    paddingRight: '1.25rem',
    paddingBottom: '0.1rem',
    paddingLeft: '1.25rem',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };
  const videoOuter: React.CSSProperties = {
    maxWidth: $MAX_WIDTH,
    margin: '0 auto',
    boxSizing: 'border-box',
    padding: '0 1.25rem 5rem',
    width: '100%',
  };
  const chipWrap: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    justifyContent: 'center',
    marginBottom: '3rem',
    maxWidth: 820,
  };

  return (
    <div style={pageStyle}>
      <section style={heroStyle}>
        <h2
          style={{
            fontSize: '2.3rem',
    fontWeight: 900,
    marginBottom: '1.25rem',
    lineHeight: 1.22,

    /* shiny-but-still-blue gradient */
    backgroundImage: `
      linear-gradient(
        90deg,
        #2F93FF 0%,
        #47A7FF 20%,
        #47A7FF 45%,   /* bright highlight, still blue */
        #47A7FF 55%,
        #47A7FF 80%,
        #1F87FF 100%
      )
    `,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',

          }}
        >
          {t.tagline}
        </h2>
<div style={{height:15}}/>
        <div style={chipWrap}>
          {t.features.map((txt) => (
            <div
              key={txt}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0.6rem 1rem',
                fontSize: '0.9rem',
                fontWeight: 600,
                background: '#FFFFFF',
                borderRadius: 16,
                boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
                border: '1px solid rgba(10,124,255,0.15)',
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0A7CFF"
                strokeWidth="3"
                strokeLinecap="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {txt}
            </div>
          ))}
        </div>
      </section>

      <main style={videoOuter}>
        <div
          style={{
            borderRadius: 22,
            padding: 2,
            background: 'linear-gradient(135deg,#0A7CFF 0%,#005EEA 100%)',
          }}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: 20,
              boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
              overflow: 'hidden',
            }}
          >
            <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
              {token && (
                <iframe
                  ref={iframeRef}
                  src={`https://iframe.videodelivery.net/${token}?poster=${posterEncoded}&playsinline=true`}
                  allow="accelerometer; gyroscope; picture-in-picture;"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    zIndex: 0,
                  }}
                />
              )}
              {showOverlay && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    height: '100%',
                    width: '100%',
                    zIndex: 2,
                  }}
                >
                  <img
                    src={t.thumb}
                    alt="Thumbnail"
                    loading="lazy"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0,0,0,0.35)',
                    }}
                  >
                    {spinner}
                  </div>
                </div>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.9rem',
                color: '#133A6A',
                background: 'rgba(236,244,255,0.6)',
                padding: '0.9rem 1.2rem',
              }}
            >
              <span style={{ flex: 1 }} />
              <a
                href={t.video}
                target="_blank"
                download
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: '#0A7CFF',
                  textDecoration: 'none',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 3v12m0 0l4-4m-4 4l-4-4M5 17h14"
                    stroke="#0A7CFF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t.download}
              </a>
            </div>
          </div>
        </div>
      </main>

      <footer
        style={{
          textAlign: 'center',
          fontSize: '0.8rem',
          color: '#133A6A',
          paddingBottom: '3rem',
        }}
      >
        © {new Date().getFullYear()}, BusinessA
      </footer>
    </div>
  );
};

export default InvestorDemoPage;
